import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Printer, RotateCcw, Ban } from 'lucide-react'
import { getInvoiceWithItems, voidInvoice } from '../services/data'
import { PageHeader, Card, DataTable, Spinner, Button } from '../components/shared/ui'
import { currency, dateTime } from '../lib/format'
import { printHtml } from '../lib/print'
import { THOMSONS_LOGO } from '../lib/assets'
import { useRole } from '../context/RoleContext'
import ManagerOverride from '../components/shared/ManagerOverride'
import { toast } from 'react-toastify'
import type { Invoice, InvoiceItem, Payment } from '../types'

export default function InvoiceDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<{ invoice: Invoice; items: InvoiceItem[]; payments: Payment[] } | null>(null)
  const [loading, setLoading] = useState(true)
  const [overridePrompt, setOverridePrompt] = useState(false)
  const { can } = useRole()

  useEffect(() => {
    if (!id) return
    setLoading(true)
    getInvoiceWithItems(id).then(setData).finally(() => setLoading(false))
  }, [id])

  if (loading) return <Spinner label="Loading invoice…" />
  if (!data) return <div className="text-gray-500">Invoice not found.</div>

  const { invoice, items, payments } = data

  const performVoid = async () => {
    try {
      const updated = await voidInvoice(invoice.id)
      setData({ ...data, invoice: updated })
      toast.success('Invoice voided')
    } catch (e) { console.error(e); toast.error('Failed to void invoice') }
  }

  const doVoid = async () => {
    if (invoice.is_void) return
    if (!window.confirm(`Void invoice #${invoice.legacy_invoice_number ?? ''}? This cannot be undone.`)) return
    // Voiding a past transaction is permission-gated (legacy perm Void_Transaction
    // / Void_Past_Transactions). Cashiers without the flag must get a manager override.
    if (!can('perm_void_past_transactions') && !can('perm_void_transaction')) {
      setOverridePrompt(true)
      return
    }
    await performVoid()
  }

  const reprintReceipt = () => {
    const rows = items.map(i => `<tr><td>${i.code || i.scan_number || i.sku || i.legacy_product_code?.toString() || ''}</td><td>${i.product_name ?? ''}</td><td class="right">${i.qty ?? ''}</td><td class="right">${currency(i.price)}</td><td class="right">${currency(i.total_price)}</td></tr>`).join('')
    const pays = payments.map(p => `<tr><td class="label">${p.payment_method ?? ''}${p.card_type ? ` (${p.card_type})` : ''}</td><td class="right">${currency(p.amount)}</td></tr>`).join('')
    printHtml(`Receipt #${invoice.legacy_invoice_number ?? ''}`, `
      <div style="text-align:center"><img src="${THOMSONS_LOGO}" style="width:120px;height:auto" alt="Thomson's"/><h2>Thomson's Landscaping &amp; Garden Center</h2><p>Invoice #${invoice.legacy_invoice_number ?? ''}<br/>${dateTime(invoice.invoice_date)}</p></div>
      <hr/>
      <table><thead><tr><th>Code</th><th>Item</th><th class="right">Qty</th><th class="right">Price</th><th class="right">Total</th></tr></thead><tbody>${rows}</tbody></table>
      <table class="totals"><tbody>
        <tr><td class="label">Subtotal</td><td class="right">${currency(invoice.taxable_amount ?? invoice.total)}</td></tr>
        <tr><td class="label">Tax</td><td class="right">${currency(invoice.tax)}</td></tr>
        <tr><td class="label" style="font-weight:bold">Total</td><td class="right" style="font-weight:bold">${currency(invoice.total_price ?? invoice.total)}</td></tr>
        ${pays}
      </tbody></table>
      <p style="text-align:center;margin-top:24px">Thank you!</p>`)
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <Button variant="ghost" onClick={() => navigate('/invoices')}>
          <ArrowLeft size={16} /> Back to Invoices
        </Button>
        <div className="flex gap-2">
          {!invoice.is_void && <Button variant="danger" onClick={doVoid}><Ban size={16} /> Void</Button>}
          <Button variant="secondary" onClick={() => navigate('/refunds')}><RotateCcw size={16} /> Refund</Button>
          <Button onClick={reprintReceipt}><Printer size={16} /> Print Receipt</Button>
        </div>
      </div>
      <PageHeader title={`Invoice #${invoice.legacy_invoice_number ?? ''}`} subtitle={dateTime(invoice.invoice_date)} />

      {invoice.is_void && (
        <div className="mb-4 px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-semibold flex items-center gap-2">
          <Ban size={16} /> This invoice has been voided.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-semibold text-gray-800 mb-4">Line Items</h3>
          <DataTable
            rows={items}
            emptyMessage="No line items"
            columns={[
              { key: 'code', header: 'Code', render: i => i.code || i.scan_number || i.sku || i.legacy_product_code?.toString() || '—' },
              { key: 'product_name', header: 'Product', render: i => i.product_name || '—' },
              { key: 'qty', header: 'Qty', render: i => i.qty ?? 0, className: 'text-right' },
              { key: 'price', header: 'Price', render: i => currency(i.price), className: 'text-right' },
              { key: 'total_price', header: 'Total', render: i => currency(i.total_price), className: 'text-right' },
            ]}
          />
        </Card>

        <div className="space-y-6">
          <Card className="p-5">
            <h3 className="font-semibold text-gray-800 mb-4">Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{currency(invoice.taxable_amount ?? invoice.total)}</span></div>
              <div className="flex justify-between text-gray-600"><span>Tax</span><span>{currency(invoice.tax)}</span></div>
              <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-100"><span>Total</span><span>{currency(invoice.total_price ?? invoice.total)}</span></div>
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="font-semibold text-gray-800 mb-4">Payments</h3>
            {payments.length ? (
              <div className="space-y-3">
                {payments.map(p => (
                  <div key={p.id} className="text-sm border border-gray-100 rounded-lg p-3 space-y-1.5">
                    <div className="flex justify-between font-semibold text-gray-900">
                      <span>{p.payment_method || '—'}{p.card_type ? ` — ${p.card_type}` : ''}</span>
                      <span>{currency(p.amount)}</span>
                    </div>
                    {p.status && (
                      <div className="flex justify-between text-gray-500">
                        <span>Status</span>
                        <span className={p.status === 'Approved' ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>{p.status}</span>
                      </div>
                    )}
                    {p.stripe_cardholder && (
                      <div className="flex justify-between text-gray-500"><span>Cardholder</span><span className="text-gray-700">{p.stripe_cardholder}</span></div>
                    )}
                    {p.stripe_last4 && (
                      <div className="flex justify-between text-gray-500"><span>Card</span><span className="text-gray-700">••••&nbsp;{p.stripe_last4}</span></div>
                    )}
                    {p.check_number && (
                      <div className="flex justify-between text-gray-500"><span>Check #</span><span className="text-gray-700">{p.check_number}</span></div>
                    )}
                    {p.amount_tendered != null && p.change_due != null && p.change_due > 0 && (
                      <>
                        <div className="flex justify-between text-gray-500"><span>Tendered</span><span>{currency(p.amount_tendered)}</span></div>
                        <div className="flex justify-between text-gray-500"><span>Change</span><span>{currency(p.change_due)}</span></div>
                      </>
                    )}
                    {p.transaction_date && (
                      <div className="flex justify-between text-gray-500"><span>Date</span><span>{dateTime(p.transaction_date)}</span></div>
                    )}
                    {p.register_number && (
                      <div className="flex justify-between text-gray-500"><span>Register</span><span>{p.register_number}</span></div>
                    )}
                    {p.stripe_charge_id && (
                      <div className="flex justify-between text-gray-500">
                        <span>Charge ID</span>
                        <span className="font-mono text-xs text-gray-600">{p.stripe_charge_id}</span>
                      </div>
                    )}
                    {p.stripe_receipt_url && (
                      <div className="pt-1">
                        <a href={p.stripe_receipt_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-900 font-medium underline underline-offset-2">
                          View Stripe Receipt ↗
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-gray-400">No payments recorded</p>}
          </Card>
        </div>
      </div>

      {overridePrompt && (
        <ManagerOverride
          perm="perm_void_past_transactions"
          title="Manager Authorization — Void"
          message={`You do not have permission to void invoice #${invoice.legacy_invoice_number ?? ''}. A manager must authorize.`}
          onApproved={async () => { setOverridePrompt(false); await performVoid() }}
          onClose={() => setOverridePrompt(false)}
        />
      )}
    </div>
  )
}
